import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { GridRowParams } from '@material-ui/data-grid';
import { Typography, ListItem } from '@material-ui/core';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';
import { gridColumn } from '../../utils/gridUtils';
import ActionsMenu from '../../components/other/ActionsMenu';
import { FormInputTypes } from '../../utils/constants';
import { drawerStore } from '../../state/drawerStore';
import { ReactComponent as ActiveIcon } from '../../images/DLM/statusActive.svg';
import { ReactComponent as InactiveIcon } from '../../images/DLM/statusInactive.svg';
import TextLink from 'components/other/TextLink';
import {
  DLM_DETAILS_TABS,
  dlmDetailsTabParameters,
  dlmDetailsTabRoute,
  route,
} from 'utils/routing';
import dashboardStore from 'state/dashboardStore';
import { Route, Switch } from 'react-router-dom';
import { PAGES } from 'components/main/MainComponent';
import DLMDetails from './DLMDetails';
import { usePaginatedProvider } from '../../state/paginatedProviderStore';
import { Api } from '../../api';

const statusBadge = (status: string) => {
  const Icon = status === 'Active' ? ActiveIcon : InactiveIcon;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Icon />
      <Typography
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: status === 'Active' ? 'green' : 'red',
        }}
      ></Typography>
    </div>
  );
};

const DLMPage = observer(() => {
  const strategyProvider = usePaginatedProvider(undefined, undefined, false);

  const fetchStrategies = async (offset: number, sortBy: string, sortOrder: 'ASC' | 'DESC') => {
    try {
      return await Api.charge.listDLMStrategies({ skip: offset, take: 20, sortBy, sortOrder });
    } catch (error) {
      console.error('Error setting up DLM strategies provider:', error);
    }
  };

  useEffect(() => {
    strategyProvider.setRequest((offset, sortBy, sortOrder) =>
      fetchStrategies(offset, sortBy, sortOrder),
    );
    strategyProvider.setSort('name', 'ASC');
  }, []);

  dashboardStore.appBarLabel = 'DLM Strategies';

  const dlmDrawerInputs = [
    { name: 'name', label: 'Name', type: FormInputTypes.textField },
    { name: 'description', label: 'Description', type: FormInputTypes.textField },
    {
      name: 'status',
      label: 'Status',
      type: FormInputTypes.select,
      options: ['Active', 'Inactive'],
    },
  ];

  const handleEdit = (row: any) => {
    drawerStore.openDrawer({
      label: 'Edit DLM Strategy',
      inputs: dlmDrawerInputs,
      clickCallback: () => {
        Api.charge
          .updateDLMStrategy(row.id, drawerStore.values)
          .then(() => strategyProvider.reloadResource());
      },
    });
    drawerStore.setValues(row);
  };

  const handleAdd = () => {
    drawerStore.openDrawer({
      label: 'Add DLM Strategy',
      inputs: dlmDrawerInputs,
      clickCallback: () => {
        Api.charge
          .createDLMStrategy(drawerStore.values)
          .then(() => strategyProvider.reloadResource());
      },
    });
    drawerStore.setValues({});
  };

  const handleRemove = (row: any) => {
    Api.charge.deleteDLMStrategy(row.id).then(() => strategyProvider.reloadResource());
  };

  const dlmActions = (params: GridRowParams) => (
    <ActionsMenu>
      <ListItem button onClick={() => handleEdit(params.row)}>
        <Typography>Edit</Typography>
      </ListItem>
      <ListItem button onClick={() => handleRemove(params.row)}>
        <Typography>Remove</Typography>
      </ListItem>
    </ActionsMenu>
  );

  const dlmColumns = [
    gridColumn('name', {
      flex: 2,
      headerName: 'Name',
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
          <TextLink
            to={dlmDetailsTabParameters(params.row.id, DLM_DETAILS_TABS.OVERVIEW)}
            label={params.row.name}
            divStyle={{ marginBottom: '0.375rem' }}
          />
        </div>
      ),
    }),
    gridColumn('description', {
      flex: 3,
      headerName: 'Description',
      minWidth: 150,
    }),
    gridColumn('status', {
      flex: 2,
      headerName: 'Status',
      minWidth: 150,
      renderCell: (params: GridRowParams) => statusBadge(params.row.status),
    }),
    gridColumn('usedBy', {
      flex: 2,
      headerName: 'Used by',
      minWidth: 150,
      valueGetter: (params: GridRowParams) => `${params.row.usedBy} charge points`,
    }),
    gridColumn('', {
      renderCell: (params: GridRowParams) => dlmActions(params),
      width: 60,
      align: 'left',
      headerAlign: 'left',
    }),
  ];

  return (
    <div style={{ width: '100%' }}>
      <VMPaginatedGrid
        rows={strategyProvider.data}
        columns={dlmColumns}
        label="DLM Strategies"
        count={strategyProvider.count}
        onClickAdd={handleAdd}
        rowHeight={60}
        onPageOffsetChange={strategyProvider.onPageChange}
      />
    </div>
  );
});

const DlmRoutes = () => (
  <Switch>
    <Route path={dlmDetailsTabRoute()} component={DLMDetails} />
    <Route path={route(PAGES.DLM)} component={DLMPage} />
  </Switch>
);
export default DlmRoutes;
